import React from "react";
import { abrirArchivoSeguro } from "../utils/upload";
import { nombreMetodo } from "../utils/pagosUi";

// Monto que hay que pagar según lo aprobado: el neto si el proveedor tiene retenciones; si no, el total.
export const montoAprobadoPago = (p) =>
  p.retenciones && p.retenciones.length > 0 && p.neto_pagar != null ? Number(p.neto_pagar) : Number(p.monto_total);

// "1,234.50", "1234,5" o "C$ 1234.50" → número (null si no es un monto válido).
export function leerMonto(texto) {
  let limpio = String(texto || "").replace(/[^\d.,-]/g, "");
  if (!limpio) return null;
  // Con punto y coma, la coma es de miles; con solo coma y 1-2 decimales, la coma es decimal.
  if (limpio.includes(".") && limpio.includes(",")) limpio = limpio.replace(/,/g, "");
  else if (/,\d{1,2}$/.test(limpio)) limpio = limpio.replace(",", ".");
  else limpio = limpio.replace(/,/g, "");
  const n = Number(limpio);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export const montosCoinciden = (a, b) => a != null && Math.abs(Math.round(a * 100) - Math.round(b * 100)) <= 1;

// Vista previa antes de registrar un pago: resumen completo y comparación del monto que
// escribió quien paga con el monto aprobado. Si no coinciden, no deja confirmar.
function ConfirmarPagoModal({ pago, metodo, referencia, banco, fecha, comprobante, observaciones, montoPagado, guardando, error, onConfirmar, onCerrar }) {
  const simbolo = pago.moneda === "USD" ? "US$" : "C$";
  const dinero = (n) => `${simbolo}${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const aprobado = montoAprobadoPago(pago);
  const escrito = leerMonto(montoPagado);
  const coincide = montosCoinciden(escrito, aprobado);
  const retenciones = pago.retenciones || [];

  const fila = (etiqueta, valor, destacado) =>
    valor ? (
      <div style={{ ...estilos.fila, ...(destacado ? estilos.filaDestacada : {}) }}>
        <span>{etiqueta}</span>
        <span>{valor}</span>
      </div>
    ) : null;

  return (
    <div style={estilos.fondo} onClick={guardando ? undefined : onCerrar}>
      <div style={estilos.caja} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Confirmar pago">
        <h3 style={estilos.titulo}>🧾 Revisa el pago antes de registrarlo</h3>
        <p style={estilos.subtitulo}>{pago.proveedor_nombre}</p>

        <div style={estilos.seccion}>
          <div style={estilos.tituloSeccion}>Monto</div>
          {fila("Total de la factura", dinero(pago.monto_total))}
          {retenciones.map((r) => (
            <div key={r.nombre} style={estilos.fila}>
              <span>
                (−) Retención {r.nombre} {r.porcentaje}%
              </span>
              <span>−{dinero(r.monto)}</span>
            </div>
          ))}
          {fila(retenciones.length > 0 ? "Neto aprobado a pagar" : "Monto aprobado a pagar", dinero(aprobado), true)}
          {fila("Monto que escribiste", escrito != null ? dinero(escrito) : "—", true)}
        </div>

        {coincide ? (
          <div style={estilos.ok}>✓ El monto coincide con lo aprobado.</div>
        ) : (
          <div style={estilos.alerta}>
            ⚠ <strong>Revisa el monto a pagar:</strong> no concuerda con el monto aprobado.
            {escrito != null && (
              <>
                {" "}
                Escribiste {dinero(escrito)} y lo aprobado es {dinero(aprobado)} (diferencia de {dinero(Math.abs(escrito - aprobado))}).
              </>
            )}{" "}
            Corrígelo para poder registrar el pago.
          </div>
        )}

        <div style={estilos.seccion}>
          <div style={estilos.tituloSeccion}>Datos del pago</div>
          {fila("Forma de pago", nombreMetodo(metodo))}
          {fila(metodo === "cheque" ? "Número de cheque" : "Referencia", referencia)}
          {fila("Banco", banco)}
          {fila("Fecha del pago", fecha ? fecha.split("-").reverse().join("/") : "")}
          {fila(
            "Comprobante",
            comprobante ? (
              <a href="#ver-comprobante" onClick={(e) => abrirArchivoSeguro(e, comprobante)} style={estilos.link}>
                🧾 Ver comprobante
              </a>
            ) : (
              "Sin comprobante"
            )
          )}
          {fila("Observaciones", observaciones)}
        </div>

        {error && <p style={estilos.error}>{error}</p>}

        <div style={estilos.pie}>
          <button type="button" onClick={onCerrar} disabled={guardando} style={estilos.botonSecundario}>
            ← Corregir
          </button>
          <button
            type="button"
            onClick={onConfirmar}
            disabled={guardando || !coincide}
            title={coincide ? "" : "El monto no concuerda con el aprobado"}
            style={{ ...estilos.botonPrimario, opacity: coincide ? 1 : 0.5, cursor: coincide ? "pointer" : "not-allowed" }}
          >
            {guardando ? "Registrando…" : "✓ Confirmar y registrar pago"}
          </button>
        </div>
      </div>
    </div>
  );
}

const estilos = {
  fondo: { position: "fixed", inset: 0, background: "rgba(27,42,74,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: "1rem" },
  caja: { background: "#fff", borderRadius: 16, padding: "1.3rem 1.5rem", width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 16px 48px rgba(27,42,74,0.35)" },
  titulo: { margin: 0, color: "var(--tema-titulo, #0b3c60)", fontSize: "1.08rem" },
  subtitulo: { margin: "0.25rem 0 0.8rem", color: "#5b6472", fontSize: "0.88rem", fontWeight: 600 },
  seccion: { border: "1px solid var(--tema-linea, #d5deec)", borderRadius: 10, padding: "0.6rem 0.85rem", marginBottom: "0.7rem" },
  tituloSeccion: { fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--tema-titulo, #0b3c60)", marginBottom: "0.35rem" },
  fila: { display: "flex", justifyContent: "space-between", gap: "1rem", padding: "0.22rem 0", fontSize: "0.86rem", color: "#3d4a5c" },
  filaDestacada: { fontWeight: 800, color: "var(--tema-titulo, #0b3c60)", fontVariantNumeric: "tabular-nums" },
  ok: { background: "#e6f4ec", border: "1px solid #c4e3d1", color: "#1e5e3f", borderRadius: 10, padding: "0.6rem 0.8rem", fontWeight: 700, fontSize: "0.86rem", marginBottom: "0.7rem" },
  alerta: { background: "#fbe9e6", border: "1px solid #f1c2b9", borderLeft: "4px solid #a3402f", color: "#7a2e21", borderRadius: 10, padding: "0.6rem 0.8rem", fontSize: "0.85rem", marginBottom: "0.7rem" },
  link: { color: "var(--tema-primario, #2f6690)", fontWeight: 600 },
  error: { color: "#a3402f", fontSize: "0.85rem", margin: "0 0 0.5rem" },
  pie: { display: "flex", justifyContent: "flex-end", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.4rem" },
  botonSecundario: { border: "1px solid #d3d9df", background: "#fff", color: "var(--tema-titulo, #0b3c60)", borderRadius: 8, padding: "0.55rem 1rem", fontSize: "0.85rem", fontWeight: 600, cursor: "pointer" },
  botonPrimario: { border: "none", background: "#2f7a54", color: "#fff", borderRadius: 8, padding: "0.55rem 1.1rem", fontSize: "0.86rem", fontWeight: 700 },
};

export default ConfirmarPagoModal;
